import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import LoadingFallback from '../components/LoadingFallback';
import StatusChip from '../components/StatusChip';
import { api } from '../api/client';
import type { ScheduleEntry, Student } from '../api/types';

export default function MyChildrenPage() {
  const childrenQuery = useQuery<Student[]>({
    queryKey: ['parents', 'me', 'children'],
    queryFn: async () => (await api.get('/parents/me/children')).data,
  });

  const [activeId, setActiveId] = useState<number | null>(null);
  const children = childrenQuery.data ?? [];
  const active = activeId ? children.find((c) => c.id === activeId) : children[0];

  return (
    <Box>
      <PageHeader title="My children" subtitle="Schedules, events, and the latest from coaches." />
      {childrenQuery.isLoading ? (
        <LoadingFallback />
      ) : children.length === 0 ? (
        <EmptyState
          title="No children linked yet"
          description="Coaches will link your account to your child's profile, and they'll show up here."
        />
      ) : (
        <Stack spacing={2}>
          <Card>
            <CardContent sx={{ pb: 1 }}>
              <Tabs
                value={active?.id ?? children[0].id}
                onChange={(_, v) => setActiveId(v as number)}
                variant="scrollable"
                allowScrollButtonsMobile
                aria-label="Child selector"
              >
                {children.map((c) => (
                  <Tab key={c.id} value={c.id} label={c.full_name} id={`tab-child-${c.id}`} />
                ))}
              </Tabs>
            </CardContent>
          </Card>
          {active && <ChildSchedule student={active} />}
        </Stack>
      )}
    </Box>
  );
}

function ChildSchedule({ student }: { student: Student }) {
  const q = useQuery<{ entries: ScheduleEntry[] }>({
    queryKey: ['schedule', 'student', student.id],
    queryFn: async () => (await api.get(`/schedule/student/${student.id}`)).data,
  });
  const meets = new Map<number, { name: string; entries: ScheduleEntry[] }>();
  for (const e of q.data?.entries ?? []) {
    const bucket = meets.get(e.meet_id) || { name: e.meet_name, entries: [] };
    bucket.entries.push(e);
    meets.set(e.meet_id, bucket);
  }

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h6">{student.full_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {student.grade ? `Grade ${student.grade}` : ''}
            {student.bib_number ? ` · Bib ${student.bib_number}` : ''}
          </Typography>
        </CardContent>
      </Card>

      {q.isLoading ? (
        <LoadingFallback />
      ) : meets.size === 0 ? (
        <EmptyState title="Nothing on the calendar yet" description="When your coach registers your athlete, events will show up here." />
      ) : (
        Array.from(meets.entries()).map(([meetId, bucket]) => (
          <Card key={meetId}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{bucket.name}</Typography>
              <Table size="small" aria-label={`Events at ${bucket.name}`}>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Where</TableCell>
                    <TableCell>Heat / Lane</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bucket.entries.map((e) => (
                    <TableRow key={e.registration_id}>
                      <TableCell>
                        {e.scheduled_start_at
                          ? dayjs(e.scheduled_start_at).format('ddd MMM D, h:mm A')
                          : 'TBD'}
                      </TableCell>
                      <TableCell>{e.event_name} <Typography variant="caption" color="text.secondary">{e.division}</Typography></TableCell>
                      <TableCell>{e.area || e.venue_name || '—'}</TableCell>
                      <TableCell>{e.heat_number ? `H${e.heat_number}${e.lane ? ` · L${e.lane}` : ''}` : '—'}</TableCell>
                      <TableCell><StatusChip status={e.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  );
}
